import { useContext, useId, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import {
    UNSAFE_DataRouterStateContext,
    UNSAFE_ViewTransitionContext,
} from 'react-router';
import { useAtomValue } from 'jotai';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';

/**
 * One MediaQueryList per query for the whole app. A tile grid runs hundreds of
 * these hooks and React reads `getSnapshot` more than once per render, so
 * minting a fresh list per read costs thousands of `matchMedia` calls during a
 * navigation — part of it inside the frozen transition frame.
 */
const mediaQueryCache = new Map<string, MediaQueryList>();

const mediaQuery = (query: string): MediaQueryList => {
    const cached = mediaQueryCache.get(query);
    if (cached) return cached;
    const created = window.matchMedia(query);
    mediaQueryCache.set(query, created);
    return created;
};

const DESKTOP_VIEWPORT_QUERY = '(min-width: 768px)';

const subscribeToDesktopViewport = (onChange: () => void) => {
    const query = mediaQuery(DESKTOP_VIEWPORT_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};

/**
 * Whether the viewport is at or above the md breakpoint. SSR assumes desktop;
 * the claim styles this gates are inert on the server, so the only effect of
 * the assumption is a correction after hydration on mobile.
 */
export const useIsDesktopViewport = (): boolean =>
    useSyncExternalStore(
        subscribeToDesktopViewport,
        () => mediaQuery(DESKTOP_VIEWPORT_QUERY).matches,
        () => true,
    );

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const subscribeToReducedMotion = (onChange: () => void) => {
    const query = mediaQuery(REDUCED_MOTION_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};

/**
 * Reduced-motion preference. When set, tiles skip the view-transition opt-in
 * entirely (no snapshot capture at all, not just neutralised animations —
 * the `prefers-reduced-motion` CSS guard remains as belt and braces), while
 * non-motion behaviour like the mobile app-mode chrome stays active.
 */
export const usePrefersReducedMotion = (): boolean =>
    useSyncExternalStore(
        subscribeToReducedMotion,
        () => mediaQuery(REDUCED_MOTION_QUERY).matches,
        () => false,
    );

/**
 * Snapshot names shared by a tile's media box on a listing and its morph
 * counterpart on the detail page (product gallery slide 0, project hero,
 * article opening picture). Professionals sit out the morph: the profile
 * page opens on its own intro reel, so a zoomed tile would land under an
 * animation that immediately replaces it.
 * During a navigation between the two, the browser morphs one box into the
 * other instead of cross-fading them with the rest of the page (styles in
 * packages/web `tailwind.css`).
 *
 * A document may only carry one element with a given name — a duplicate makes
 * the browser skip the transition (an instant swap, not a broken frame) — so
 * claim it only while a matching navigation is in flight, and only once per
 * side of that navigation (see the claim registry below). Names are unique per
 * content type, so a page can carry a product and a project claim at once
 * without conflict.
 */
export const PRODUCT_HERO_TRANSITION_NAME = 'product-hero';
export const PROJECT_HERO_TRANSITION_NAME = 'project-hero';
export const ARTICLE_HERO_TRANSITION_NAME = 'article-hero';

/** The snapshot names a morph can be claimed under. */
export type HeroTransitionName =
    | typeof PRODUCT_HERO_TRANSITION_NAME
    | typeof PROJECT_HERO_TRANSITION_NAME
    | typeof ARTICLE_HERO_TRANSITION_NAME;

const pathnameOf = (to: string): string => {
    const end = to.search(/[?#]/);
    return end === -1 ? to : to.slice(0, end);
};

/**
 * The tile-initiated morph in flight: the page a tile points at. Recorded on
 * click and kept until a navigation that has nothing to do with it, so the one
 * record serves the forward leg and the Back leg alike. Module state, like the
 * pending hero image, so it survives the client-side navigation.
 *
 * Only the destination is recorded. The origin is not needed and could not be
 * trusted anyway: the router pushes history before React re-renders, so the
 * browser URL at click time is not reliably the page the tile sits on.
 */
interface PendingTileMorph {
    name: HeroTransitionName;
    toPathname: string;
}

let pendingTileMorph: PendingTileMorph | null = null;

/**
 * Records the tile whose click is about to start a navigation, and the exact
 * rendition its media box is showing.
 *
 * Both halves describe the same click and must be recorded together — the
 * destination settles which element claims the snapshot name, the image is
 * what the destination paints under its own hero until that decodes — so
 * there is one entry point rather than two calls to keep in step.
 */
export const recordTileMorphStart = (
    name: HeroTransitionName,
    to: string | undefined,
    imageSrc: string | null | undefined,
): void => {
    if (!to) return;
    pendingTileMorph = { name, toPathname: pathnameOf(to) };
    setPendingHeroImage(to, imageSrc);
};

/**
 * Whether a click will actually navigate in this document. A modifier click
 * fires React's `onClick`, but the router then declines to navigate and the
 * browser opens a tab instead — recording that click would leave a morph
 * pending for a navigation that never happens.
 */
export const startsPlainNavigation = (event: {
    button?: number;
    metaKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    altKey?: boolean;
}): boolean =>
    (event.button ?? 0) === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

/**
 * Whether the recorded click describes the navigation now in flight.
 *
 * The router hands both legs of a round trip the same location pair — on Back
 * it swaps the two so the animation runs in reverse under the same names — so
 * the pair cannot say which way we are going, and the record only has to name
 * a page at one end of it.
 */
const describesNavigation = (
    pending: PendingTileMorph,
    currentPathname: string,
    nextPathname: string,
): boolean =>
    pending.toPathname === currentPathname ||
    pending.toPathname === nextPathname;

/**
 * Whether an element may claim `name` at all, before the registry picks
 * between competing claimants.
 *
 * With no recorded click — a Back into a page nobody left by tile, a deep link
 * followed by a text link — everything matching an end of the navigation is
 * eligible, which is the plain path matching this hook has always done. A
 * record narrows it to the page the tile points at, which is the tie-break
 * detail-to-detail navigation needs: the tile and the destination hero both
 * describe that page, while the hero of the page being left describes the
 * other end and stands down rather than claiming the name out from under the
 * tile.
 */
const isEligibleClaimant = (
    name: string,
    target: string,
    currentPathname: string,
    nextPathname: string,
): boolean => {
    const pending = pendingTileMorph;
    if (!pending || pending.name !== name) return true;
    if (!describesNavigation(pending, currentPathname, nextPathname)) {
        return true;
    }
    return pending.toPathname === target;
};

/**
 * One holder per name, per rendered document — which is the rule the browser
 * actually enforces: a second element answering to the same name makes it skip
 * the transition outright.
 *
 * The document is identified by the location being rendered, not by the pair
 * the transition carries. The router renders the outgoing page with
 * `isTransitioning` already true and only then flushes the incoming page
 * inside `startViewTransition`, so those two passes see two different rendered
 * locations while sharing one transition — and both ends of the morph get
 * their name without either being able to take the other's. Anything cheaper
 * (which kind of element is claiming, which page the name refers to) is only a
 * proxy for the pass, and breaks as soon as a page carries both a hero and a
 * tile that match the navigation.
 *
 * The registry is scoped to the router's transition object rather than to the
 * locations it carries: Back reuses the location pair of the forward leg, so a
 * location-derived scope would leave the map holding the forward leg's tokens
 * and deny every claimant on the way back. A fresh transition object is minted
 * per navigation and shared by both of its passes, which is exactly the scope
 * wanted.
 *
 * Claims resolve during render because the name has to be on the element
 * before the browser snapshots it, in that same commit, and `useId` keys them
 * so a render React discards and retries re-claims instead of locking itself
 * out. Entries are never removed within a transition: an element that claims
 * and then never commits (a sibling suspends, the tree is torn down mid-flight)
 * holds its key until the next navigation, and the morph degrades to the plain
 * cross-fade for that one navigation.
 */
let claimHolders = new Map<string, string>();
let claimedTransition: object | null = null;

const acquireHeroSnapshotClaim = (
    transition: object,
    key: string,
    token: string,
): boolean => {
    if (claimedTransition !== transition) {
        claimedTransition = transition;
        claimHolders = new Map();
    }
    const holder = claimHolders.get(key);
    if (holder === undefined) {
        claimHolders.set(key, token);
        return true;
    }
    return holder === token;
};

/**
 * Style for the element that should morph during a view transition whose
 * origin or destination pathname equals `to` — the tile whose own navigation
 * is in flight (either direction, so Back morphs symmetrically), never its
 * neighbours, and never a second element answering to the same name.
 *
 * Reads the router's view-transition context directly rather than through
 * `useViewTransitionState`, which requires a data router and throws without
 * one. The context defaults to `{ isTransitioning: false }`, so outside a
 * data router (Business Manager, unit tests) this is simply inert — which is
 * also why the module state above never leaks across SSR requests.
 */
export const useTileHeroViewTransitionStyle = (
    to: string | undefined,
    name: HeroTransitionName = PRODUCT_HERO_TRANSITION_NAME,
): CSSProperties | undefined => {
    const enabled = useAtomValue(uiContextAtomViewTransitionsEnabled);
    const reducedMotion = usePrefersReducedMotion();
    const vt = useContext(UNSAFE_ViewTransitionContext);
    const routerState = useContext(UNSAFE_DataRouterStateContext);
    const claimToken = useId();
    if (!enabled || reducedMotion || !vt.isTransitioning || !routerState) {
        return undefined;
    }

    const currentPathname = vt.currentLocation.pathname;
    const nextPathname = vt.nextLocation.pathname;
    // A record that has nothing to say about this navigation is spent: drop it
    // rather than let it narrow eligibility on some later trip between the
    // same two pages.
    if (
        pendingTileMorph &&
        !describesNavigation(pendingTileMorph, currentPathname, nextPathname)
    ) {
        pendingTileMorph = null;
    }
    if (!to) return undefined;

    const target = pathnameOf(to);
    if (target !== currentPathname && target !== nextPathname) {
        return undefined;
    }
    if (!isEligibleClaimant(name, target, currentPathname, nextPathname)) {
        return undefined;
    }
    return acquireHeroSnapshotClaim(
        vt,
        `${routerState.location.key}:${name}`,
        claimToken,
    )
        ? { viewTransitionName: name }
        : undefined;
};

/**
 * Image continuity for a cold cache: the destination gallery renders a
 * different (larger) rendition than the tile, so on first open the morph
 * would land on a grey placeholder until the high-res image decodes. The
 * tile's click handler records the exact rendition its <img> is showing —
 * already decoded, by definition — and the gallery paints it under its own
 * image until the real one arrives. Module state, so it survives the
 * client-side navigation and costs nothing on SSR or full loads.
 */
let pendingHeroImage: { pathname: string; src: string } | null = null;

const setPendingHeroImage = (
    to: string | undefined,
    src: string | null | undefined,
): void => {
    if (!to || !src) return;
    pendingHeroImage = { pathname: pathnameOf(to), src };
};

export const getPendingHeroImage = (to: string): string | null => {
    const pending = pendingHeroImage;
    return pending && pending.pathname === pathnameOf(to) ? pending.src : null;
};

/**
 * Clears every module-level record this file keeps, including the cached media
 * queries so a test can stub `matchMedia`. Tests only — the browser has no
 * reason to reset, and each piece is already scoped to the navigation or
 * transition it belongs to.
 */
export const resetTileMorphStateForTests = (): void => {
    pendingTileMorph = null;
    pendingHeroImage = null;
    claimHolders = new Map();
    claimedTransition = null;
    mediaQueryCache.clear();
};
